import { BasicButton, Button, PrimaryButton, TextButton } from '../../../../widgets/buttons';
import { PageFooterButtons } from '../../../../widgets/headers';
import { PageHeader } from '../../../../widgets/pageHeader';
import { VerticalLayout } from '../../../../widgets/layouts';
import _ from 'lodash';
import React, { FunctionComponent, useMemo } from 'react';
import { Section } from 'src/components/widgets/section';
import { Center } from 'src/shared/globals';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { useProductConfigurationState } from '../../state';
import { ModelColor, isDefaultColor as isDefaultModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import VariantRow from './variantRow';

const MappingLinkButton = styled(TextButton)`
    margin-right: 25px;
`;

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

interface VariantsListProps {
    isSidesPerVariantEnabled: boolean;
    onSideAreaChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideImageChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideToggleClick: (colorGuid: string, sideGuid: string) => void;
    onVariantRemoveClick: (modelColor: ModelColor) => void;
    onVariantMoveUpClick: (modelColor: ModelColor) => void;
    onVariantMoveDownClick: (modelColor: ModelColor) => void;
    onVariantEditClick: (modelColor: ModelColor) => void;
    onVariantAddClick: (showConnectModelDialog?: boolean) => void;
    onPreviewClick: () => void;
    onSetMandatorySide: (value: boolean, modelSide: ModelSide) => void;
    onSetRotateSide: (value: boolean, model: ModelSide) => void;
    onClickJsonSettings: (modelColor: ModelColor) => void;
    unsupportedSalesChannelsVariantsIDs: string[];
}

const VariantsList: FunctionComponent<VariantsListProps> = ({
    isSidesPerVariantEnabled,
    onSideImageChangeClick,
    onSideAreaChangeClick,
    onSideToggleClick,
    onVariantEditClick,
    onVariantRemoveClick,
    onVariantMoveDownClick,
    onVariantMoveUpClick,
    onVariantAddClick,
    onPreviewClick,
    onSetMandatorySide,
    onSetRotateSide,
    onClickJsonSettings,
    unsupportedSalesChannelsVariantsIDs
}) => {
    const { state } = useProductConfigurationState();
    const colors = useMemo(
        () =>
            _.sortBy(
                state.product.colors.filter((x) => !isDefaultModelColor(x)),
                (color) => color.position
            ),
        [state.product.colors]
    );

    const additionalCodesFiltered = useMemo(() => {
        return state.product.additionalCodes.filter((ac) => {
            return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
        });
    }, [state.product.additionalCodes, unsupportedSalesChannelsVariantsIDs]);

    return (
        <React.Fragment>
            <VerticalLayout>
                <PageHeaderContainer>
                    <PageHeader
                        isBackVisible={false}
                        title={state.product.name}
                        actions={
                            <>
                                {colors.length > 0 && additionalCodesFiltered.length > 0 && (
                                    <MappingLinkButton onClick={() => onVariantAddClick(true)}>{T._('Mapping Sales Channel', 'Admin')}</MappingLinkButton>
                                )}
                                {colors.length > 0 && (
                                    <BasicButton onClick={() => onVariantAddClick()}>
                                        {state.ecommerceSalesChannel.some((e) => e === 'NONE') ? T._('Edit attributes', 'Admin') : T._('Add', 'Admin')}
                                    </BasicButton>
                                )}
                            </>
                        }
                    />
                </PageHeaderContainer>

                <Section
                    description={
                        colors.length > 0 ? (
                            <>
                                {T._(`Pick the images your buyers will see as they customize their orders.`, 'Admin')}
                                <br />
                                {T._(`Zakeke needs one image for each variation.`, 'Admin')}
                            </>
                        ) : null
                    }
                >
                    {colors.map((modelColor, index) => {
                        return (
                            <VariantRow
                                key={index}
                                color={modelColor}
                                showMoveDownButton={index != colors.length - 1}
                                showMoveUpButton={index != 0}
                                showEditButton={true}
                                onClickJsonSettings={() => onClickJsonSettings(modelColor)}
                                onMoveDownClick={() => onVariantMoveDownClick(modelColor)}
                                onMoveUpClick={() => onVariantMoveUpClick(modelColor)}
                                isSidesPerVariantEnabled={isSidesPerVariantEnabled}
                                onToggleClick={onSideToggleClick}
                                onAreaChangeClick={onSideAreaChangeClick}
                                onImageChangeClick={onSideImageChangeClick}
                                onRemoveClick={() => onVariantRemoveClick(modelColor)}
                                onEditClick={() => onVariantEditClick(modelColor)}
                                onSetMandatorySide={onSetMandatorySide}
                                onSetRotateSide={onSetRotateSide}
                                showRemoveButton={!(state.product.colors.length == 1 && state.product.colors[0].colorID == 1)}
                            />
                        );
                    })}

                    {colors.length == 0 && (
                        <div
                            css={`
                                margin: 40px 0;
                            `}
                        >
                            <Center>
                                <PrimaryButton size='large' onClick={() => onVariantAddClick()}>
                                    {state.ecommerceSalesChannel.some((e) => e === 'NONE') && state.ecommerceSalesChannel.length == 1
                                        ? T._('Edit attributes', 'Admin')
                                        : T._('Add variation', 'Admin')}
                                </PrimaryButton>
                            </Center>
                        </div>
                    )}
                </Section>
            </VerticalLayout>

            {/* {state.product.additionalCodes.length == 0 && colors.length > 0 && <BasicButton onClick={() => onVariantAddClick()}>{(state.ecommerceSalesChannel.some(e=>e === 'NONE') && state.ecommerceSalesChannel.length == 1) ? T._('Go to variations','Admin') : T._('Add', 'Admin')}</BasicButton>} */}
            {colors.length > 0 && (
                <PageFooterButtons $fixed={true}>
                    <Button size='large' onClick={onPreviewClick}>
                        {T._(`Go to review`, 'Admin')}
                    </Button>
                </PageFooterButtons>
            )}
        </React.Fragment>
    );
};

export default VariantsList;
